<template>
  <account-layout>
    <el-form :model="loginForm" :rules="loginRules" ref="loginForm">
      <el-row :gutter="60">
        <el-col :span="24" v-if="!requires2FA">
          <div>Email</div>
          <el-form-item prop="email">
            <el-input class="input-space" type="email" v-model="loginForm.email" placeholder="Type here..."  @keyup.enter.native="login" />
          </el-form-item>
        </el-col>
        <el-col v-if="(!usingSSO || showPasswordField) && !requires2FA" :span="24">
          <div>Password</div>
          <el-form-item prop="password">
            <el-input class="input-space" type="password" v-model="loginForm.password" placeholder="Type here..."  @keyup.enter.native="login"/>
          </el-form-item>
        </el-col>
        <el-col :span="24" v-if="requires2FA">
          <div>Verification Code</div>
          <el-form-item prop="verificationCode">
            <el-input class="input-space" v-model="loginForm.verificationCode" placeholder="Enter 6-digit code" @keyup.enter.native="submit2FACode" />
          </el-form-item>
          <div class="verification-message">{{ verificationMessage }}</div>
        </el-col>
        <el-col :span="24" v-if="errorMessage">
          <el-alert show-icon type="error" :title="errorMessage" :closable="false" />
        </el-col>
        <el-col :span="24" v-if="successMessage">
          <el-alert show-icon type="success" :title="successMessage" :closable="false" />
        </el-col>
      </el-row>
      <el-row class="row-space">
        <el-col :span="24">
          <el-button
              v-if="usingSSO && !requires2FA"
              type="primary"
              class="ac-btn"
              @click="verified ? (!showPasswordField ? loginSSO() : login()) : verify()"
            >{{ verified ? (!showPasswordField ? 'Login with SSO' : 'Login') : 'Next' }}
          </el-button>
          <el-button
              v-else-if="requires2FA"
              type="primary"
              class="ac-btn"
              @click="submit2FACode"
            >Verify
          </el-button>
          <el-button
              v-else
              type="primary"
              class="ac-btn"
              @click="verified ? login() : verify()"
            >{{ verified ? 'Login' : 'Next' }}
          </el-button>
        </el-col>
      </el-row>
    </el-form>
    <el-row v-if="!requires2FA">
      <el-col :span="12">
        <el-button type="text" @click="reset">Forgot password?</el-button>
      </el-col>
      <el-col :span="12" class="align-right">
        <el-button type="text" @click="signUp">Sign Up</el-button>
      </el-col>
    </el-row>
    <el-row v-else>
      <el-col :span="12">
        <el-button type="text" @click="goBack">Go Back</el-button>
      </el-col>
      <el-col :span="12" class="align-right">
        <el-button
          type="text"
          :disabled="resendCooldown > 0 || resendingCode"
          @click="resend2FACode"
        >
          {{ resendingCode ? 'Sending...' : (resendCooldown > 0 ? `Resend Code (${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, '0')})` : 'Resend Code') }}
        </el-button>
      </el-col>
    </el-row>
  </account-layout>
</template>

<script>
import _ from 'lodash';
import { datadogRum } from '@datadog/browser-rum';
import AccountLayout from './AccountLayout.vue';

export default {
  name: 'AccountLogin',
  components: {
    AccountLayout
  },
  props: {
    platform: String
  },
  data() {
    const checkEmail = (rule, value, callback) => {
      if (!value) {
        this.showPasswordField = false;
        return callback();
      }
      const re = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
      if (re.test(String(value).toLowerCase())) {
        if (value.match('^.*@assemblesports.(io|com|xyz)$')) {
          this.showPasswordField = true;
        } else if (this.usingSSO) {
          this.showPasswordField = false;
        }
        return callback();
      }

      if (this.usingSSO) {
        this.showPasswordField = false;
      }

      return callback(new Error('Invalid email address'));
    };

    const usingSSO = process.env.VUE_APP_NATIONAL_ID === '20'; // Bond University

    const { token } = this.$route?.query || {};

    if (token) {
      try {
        const decodedToken = JSON.parse(Buffer.from(token, 'base64').toString('ascii'));

        this.$store.commit('loading', true);
        this.$store.dispatch('user/login', decodedToken)
          .then(() => {
            if (this.$store.getters['user/roles'].length) {
              Promise.all([
                this.$store.dispatch('user/fetchModule'),
                this.$store.dispatch('national/setup'),
                this.$store.dispatch('season/setup')
              ]).then(() => {
                this.$router.push('dashboard').catch(() => { this.$router.go(0); });
              });
            } else {
              this.$router.push('pending');
            }
          });
      } catch (e) {
        datadogRum.addError(e, {
          message: 'Error decoding token',
          token
        });
      }
      this.$store.commit('loading', false);
    }

    return {
      errorMessage: '',
      loginForm: {
        email: '',
        password: '',
        verificationCode: ''
      },
      loginRules: {
        email: [
          { validator: checkEmail, trigger: 'blur' }
        ],
        password: [
          { required: true, message: 'Password is required', trigger: 'blur' }
        ],
        verificationCode: [
          { required: false }
        ]
      },
      verified: true,
      usingSSO,
      showPasswordField: !usingSSO,
      requires2FA: false,
      userId: null,
      verificationMessage: '',
      successMessage: '',
      resendCooldown: 0,
      resendTimer: null,
      resendingCode: false
    };
  },
  mounted() {
    // if user already logged in, redirect to dashboard
    const isAuthenticated = this && this.$store && this.$store.getters['user/isAuthenticated'];
    if (isAuthenticated) {
      this.$router.push('dashboard').catch(() => {});
      return;
    }
    // if email & password passed in url, log user in
    const { email } = this.$route.query;
    const { password } = this.$route.query;
    if (email && password) {
      this.loginForm.email = email;
      this.loginForm.password = password;
      this.login();
    }
  },
  methods: {
    loginSSO() {
      if (!this.usingSSO) return;
      this.$store.commit('loading', true);
      window.location.href = `${process.env.VUE_APP_API_URL}/assemble/api/v1/account/${this.platform}/sso`;
    },
    login() {
      this.errorMessage = '';
      this.$refs.loginForm.validate(async (valid) => {
        if (valid) {
          try {
            this.$store.commit('loading', true);
            // const activeRole = localStorage.getItem('activeRole');
            const res = await this.$http.post(`/assemble/api/v1/account/${this.platform}/login`, {
              email: this.loginForm.email,
              password: this.loginForm.password,
              // activeRole
            });

            if (_.get(res, 'data.data.resetHash')) {
              this.$router.push({
                name: 'reset.submit',
                params: {
                  token: res.data.data.resetHash
                }
              });
            } else if (res.data?.data?.requireTwoFactor) {
              // Handle 2FA requirement
              this.requires2FA = true;
              this.showPasswordField = false;
              this.userId = res.data.data.userId;

              // Reset any validation errors since we're moving to 2FA stage
              this.$refs.loginForm.clearValidate();

              // Show a message about the verification code being sent
              this.verificationMessage = 'A verification code has been sent to your email.';

              // Start the resend cooldown timer
              this.startResendCooldown();

              this.$store.commit('loading', false);
            } else {
              this.$store.commit('user/login', res.data);
              await this.$store.dispatch('national/setup');
              this.$store.commit('finder/clearFitlers');
              this.$router.push('dashboard').catch(() => {});
            }
            this.$store.commit('loading', false);
          } catch (e) {
            if (e?.response?.status === 401 && e?.response?.data?.verified === false) {
              this.verified = false;
            }
            if (e.name === 'NavigationDuplicated') {
              this.$router.go(0);
            } else {
              this.errorMessage = e?.response?.data?.message || 'An error has occured';
            }
            datadogRum.addError(e, {
              message: 'Error logging in',
              email: this.loginForm.email
            });
            this.$store.commit('loading', false);
          }
        }
      });
    },
    async submit2FACode() {
      this.errorMessage = '';
      this.successMessage = '';

      // Manually validate the verification code
      if (!this.loginForm.verificationCode || this.loginForm.verificationCode.trim() === '') {
        this.errorMessage = 'Please enter the verification code';
        return;
      }

      try {
        this.$store.commit('loading', true);
        const res = await this.$http.post(`/assemble/api/v1/account/${this.platform}/verify-2fa`, {
          userId: this.userId,
          code: this.loginForm.verificationCode
        });

        this.$store.commit('user/login', res.data);
        await this.$store.dispatch('national/setup');
        this.$store.commit('finder/clearFitlers');
        this.$router.push('dashboard').catch(() => {});
        this.$store.commit('loading', false);
      } catch (e) {
        const errorMessage = e?.response?.data?.message || 'Invalid or expired verification code';

        // Check if the error is specifically about an expired code
        if (errorMessage.toLowerCase().includes('expired') || e?.response?.status === 410) {
          this.errorMessage = 'Your verification code has expired. Please click "Resend Code" to get a new one.';
        } else if (errorMessage.toLowerCase().includes('invalid')) {
          this.errorMessage = 'Invalid verification code. Please check the code and try again.';
        } else {
          this.errorMessage = errorMessage;
        }

        datadogRum.addError(e, {
          message: 'Error verifying 2FA code',
          userId: this.userId
        });
        this.$store.commit('loading', false);
      }
    },
    verify() {
      this.$router.push('verify');
    },
    reset() {
      this.$router.push('reset');
    },
    signUp() {
      const pathName = this.hasCustomEntity ? 'custom-sign-up' : 'sign-up';
      this.$router.push({ name: pathName });
    },
    goBack() {
      this.requires2FA = false;
      this.showPasswordField = !this.usingSSO;
      this.errorMessage = '';
      this.successMessage = '';
      this.verificationMessage = '';
      this.loginForm.verificationCode = '';

      // Clear the resend timer
      this.clearResendTimer();
    },
    async resend2FACode() {
      // Prevent multiple rapid clicks
      if (this.resendingCode) {
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';

      // Check if still in cooldown period
      if (this.resendCooldown > 0) {
        this.errorMessage = `Please wait ${Math.floor(this.resendCooldown / 60)}:${String(this.resendCooldown % 60).padStart(2, '0')} before requesting a new code.`;
        return;
      }

      if (!this.userId) {
        this.errorMessage = 'Unable to resend code. Please try logging in again.';
        return;
      }

      try {
        this.resendingCode = true;
        this.$store.commit('loading', true);
        await this.$http.post(`/assemble/api/v1/account/${this.platform}/resend-2fa`, {
          userId: this.userId
        });

        this.successMessage = 'A new verification code has been sent to your email.';
        this.loginForm.verificationCode = '';

        // Restart the resend cooldown timer
        this.startResendCooldown();

        this.$store.commit('loading', false);
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || 'Failed to resend verification code. Please try again.';
        this.$store.commit('loading', false);
      } finally {
        this.resendingCode = false;
      }
    },
    startResendCooldown() {
      this.resendCooldown = 120; // 2 minutes in seconds
      this.resendTimer = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) {
          this.clearResendTimer();
        }
      }, 1000);
    },
    clearResendTimer() {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
      this.resendCooldown = 0;
    }
  },
  computed: {
    hasCustomEntity() {
      const customEntity = this.$store.getters['national/getEntity'];
      if (customEntity && customEntity.level && customEntity.name) {
        return true;
      }
      return false;
    },
  },
  beforeDestroy() {
    this.clearResendTimer();
  }
};
</script>

<style scoped lang="scss">
.content-card {
  margin-left: 1em;
  margin-right: 1em;
  margin-top: 2em;
  width: 90%;
  max-width: 30em;
}

.align-right {
  text-align: right;
}

.row-space {
  margin-top: 1em;
}

.input-space {
  margin-top: 0.5em;
}

.ac-btn {
  width: 100%;
  background-color: $highlight !important;
  border: 0;
}
.secondary {
  background-color: $secondary !important;
  color: #fff;
  border: 0;
}
.verification-message {
  font-size: 0.9em;
  color: #606266;
  margin-top: 5px;
  margin-bottom: 10px;
}
</style>
